import React from 'react';
import PropTypes from 'prop-types';
import { connect, useDispatch, useSelector } from 'react-redux';
import styled from 'styled-components';

import { Button, Modal } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';

import { colors } from '../../utils/variables.styles';

import EditableSection from '../../components/EditableSection';
import SelectEdit from '../../components/SelectEdit';
import RangeEdit from '../../components/RangeEdit/RangeEdit';

import selector from './shutterWindowsEditor.selector';

import { infoShape } from '../../types/info.types';
import { availableType } from '../../types/available.types';

import {
  selectFirstHeight,
  selectSecondHeight,
  selectLength,
  selectWidth,
  setSelected as setSelectedParameter,
  setWindows
} from '../Construction/construction.reducer';
import withTranslation from '../../components/WithTranslation/WithTranslation';

const Container = styled.div`
  background: ${colors.white};
  padding: 10px 24px;
  margin-left: -27px;
`;

const Spacer = styled.div`
  padding: 10px 24px;
  font-weight: 600;
  max-width: 280px;
`;

const ImgWrapper = styled.img`
  max-width: 100%;
  display: block;
  margin: 15px auto;
`;

const ButtonWrapper = styled.div`
  padding-top: 5px;
`;

/* =========================
   Helpers (safe)
   ========================= */
const asArray = (v) => (Array.isArray(v) ? v : []);

const findByIdSafe = (list, id) => {
  const arr = asArray(list);
  const sid = id == null ? '' : String(id);
  return arr.find((el) => String(el?.id) === sid);
};

// Parse size like "80x60" or from numeric width/height
const getSelectedSize = (available, selectedId) => {
  const opt = findByIdSafe(available, selectedId);
  if (!opt) return { width: 0, height: 0 };

  if (typeof opt.width === 'number' && typeof opt.height === 'number') {
    return { width: opt.width, height: opt.height };
  }

  const raw = String(opt.value ?? opt.label ?? opt.name ?? '').toLowerCase();
  const m = raw.match(/(\d+(?:\.\d+)?)\s*[x×]\s*(\d+(?:\.\d+)?)/i);
  if (m) return { width: Number(m[1]) || 0, height: Number(m[2]) || 0 };

  const nums = (raw.match(/\d+(?:\.\d+)?/g) || []).map(Number);
  if (nums.length >= 2) return { width: nums[0] || 0, height: nums[1] || 0 };

  return { width: 0, height: 0 };
};

// Count items from available based on selected option text/number
const getCountFromAvailable = (available, selectedId) => {
  const opt = findByIdSafe(available, selectedId);
  if (!opt) return 0;

  const raw = String(opt.value ?? opt.label ?? opt.name ?? '').toLowerCase();
  const firstNum = raw.match(/-?\d+/);
  if (firstNum) {
    const n = parseInt(firstNum[0], 10);
    if (!Number.isNaN(n)) return Math.max(0, n);
  }
  if (raw.includes('brak') || raw.includes('zero')) return 0;
  if (raw.includes('jeden') || raw.includes('1')) return 1;
  if (raw.includes('dwa') || raw.includes('2')) return 2;
  return 0;
};

const ConstructionExtras = ({
                              collapsed,
                              loaded = false,

                              // BACK
                              shutter_back_count_available = [],
                              shutter_back_count_info = null,
                              shutter_back_count_selected = null,
                              shutter_back_size_available = [],
                              shutter_back_size_info = null,
                              shutter_back_size_selected = null,
                              shutter_back_material_available = [],
                              shutter_back_material_info = null,
                              shutter_back_material_selected = null,

                              // LEFT
                              shutter_left_count_available = [],
                              shutter_left_count_info = null,
                              shutter_left_count_selected = null,
                              shutter_left_size_available = [],
                              shutter_left_size_info = null,
                              shutter_left_size_selected = null,
                              shutter_left_material_available = [],
                              shutter_left_material_info = null,
                              shutter_left_material_selected = null,

                              // RIGHT
                              shutter_right_count_available = [],
                              shutter_right_count_info = null,
                              shutter_right_count_selected = null,
                              shutter_right_size_available = [],
                              shutter_right_size_info = null,
                              shutter_right_size_selected = null,
                              shutter_right_material_available = [],
                              shutter_right_material_info = null,
                              shutter_right_material_selected = null,

                              setSelected,
                              t
                            }) => {
  const openModal = () => {
    Modal.info({
      title: shutter_back_count_info?.info_title,
      content: (
        <>
          {shutter_back_count_info ? (
            <>
              {shutter_back_count_info.img_one ? (
                <ImgWrapper src={shutter_back_count_info.img_one} />
              ) : null}
              {shutter_back_count_info.img_two ? (
                <ImgWrapper src={shutter_back_count_info.img_two} />
              ) : null}
              {shutter_back_count_info.info_content}
              {shutter_back_count_info.img_three ? (
                <ImgWrapper src={shutter_back_count_info.img_three} />
              ) : null}
              {shutter_back_count_info.img_four ? (
                <ImgWrapper src={shutter_back_count_info.img_four} />
              ) : null}
            </>
          ) : null}
        </>
      )
    });
  };

  // Hall dims
  const width = useSelector(selectWidth);
  const length = useSelector(selectLength);
  const firstHeight = useSelector(selectFirstHeight);
  const secondHeight = useSelector(selectSecondHeight);

  // Redux
  const dispatch = useDispatch();
  const windows = useSelector((state) => state.Construction.windows);

  // Position change for one flap (type = 'openings_flaps')
  const onFlapPositionChange = (wall, index, key, value) => {
    const updated = (asArray(windows[wall]?.openings_flaps)).map((d, i) =>
      i === index ? { ...d, [key]: value } : d
    );
    dispatch(setWindows(wall, 'openings_flaps', updated));
  };

  // COUNT change: create/update entries with current selected size & material
  const handleFlapCountChange = (
    wall,
    paramKey,
    selectedId,
    countAvailable,
    sizeAvailable,
    sizeSelectedId,
    materialSelectedId
  ) => {
    const flapsCount = getCountFromAvailable(countAvailable, selectedId);
    const { width: itemW, height: itemH } = getSelectedSize(sizeAvailable, sizeSelectedId);

    const current = asArray(windows[wall]?.openings_flaps);
    const updated = Array.from({ length: flapsCount }, (_, index) => {
      const existing = current[index];
      if (existing) {
        return {
          ...existing,
          width: typeof existing.width === 'number' && existing.width > 0 ? existing.width : itemW,
          height: typeof existing.height === 'number' && existing.height > 0 ? existing.height : itemH,
          materialId: existing.materialId ?? materialSelectedId ?? null,
        };
      }
      return {
        id: index,
        positionHorizontal: 20,
        positionVertical: 50,
        width: itemW,
        height: itemH,
        materialId: materialSelectedId ?? null,
      };
    });

    dispatch(setWindows(wall, 'openings_flaps', updated));
    setSelected(paramKey, selectedId);
  };

  // SIZE change: update width/height for all
  const handleFlapSizeChange = (wall, paramKey, selectedId, sizeAvailable) => {
    const { width: itemW, height: itemH } = getSelectedSize(sizeAvailable, selectedId);
    const current = asArray(windows[wall]?.openings_flaps);
    const updated = current.map((d) => ({
      ...d,
      width: itemW,
      height: itemH,
    }));

    dispatch(setWindows(wall, 'openings_flaps', updated));
    setSelected(paramKey, selectedId);
  };

  // MATERIAL change: persist materialId to all items on the wall
  const handleFlapMaterialChange = (wall, paramKey, selectedId) => {
    const current = asArray(windows[wall]?.openings_flaps);
    const updated = current.map((d) => ({
      ...d,
      materialId: selectedId,
    }));

    dispatch(setWindows(wall, 'openings_flaps', updated));
    setSelected(paramKey, selectedId);
  };

  // Render helper for walls
  const renderWallControls = (
    wallKey,
    // labels for setSelected keys
    labelCount,
    labelSize,
    labelMaterial,
    // count
    countAvailable,
    countSelected,
    countInfo,
    // size
    sizeAvailable,
    sizeSelected,
    sizeInfo,
    // material
    materialAvailable,
    materialSelected,
    materialInfo,
    // geometry
    maxHorizontalBy
  ) => (
    <>
      {/* COUNT */}
      <SelectEdit
        selected={countSelected}
        loaded={loaded}
        title={countInfo?.name}
        onChange={(id) =>
          handleFlapCountChange(
            wallKey,
            labelCount,
            id,
            countAvailable,
            sizeAvailable,
            sizeSelected,
            materialSelected
          )
        }
        options={asArray(countAvailable).map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      {/* SIZE */}
      <SelectEdit
        selected={sizeSelected}
        loaded={loaded}
        title={sizeInfo?.name}
        onChange={(id) => handleFlapSizeChange(wallKey, labelSize, id, sizeAvailable)}
        options={asArray(sizeAvailable).map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      {/* MATERIAL (kept + now persisted to items) */}
      <SelectEdit
        selected={materialSelected}
        loaded={loaded}
        title={materialInfo?.name}
        onChange={(id) => handleFlapMaterialChange(wallKey, labelMaterial, id)}
        options={asArray(materialAvailable).map((el) => ({ id: el.id, value: `${el.value}` }))}
        nested
      />

      {(asArray(windows[wallKey]?.openings_flaps)).map((item, index, arr) => (
        <React.Fragment key={`${item.id}_group`}>
          <RangeEdit
            key={`${item.id}_horizontal`}
            title={
              arr.length === 1
                ? t('hole_position_horizontal')
                : t('hole_position_horizontal') + ' 2'
            }
            min={20}
            step={5}
            max={maxHorizontalBy - 20 - item.width}
            unit={'cm'}
            selected={item.positionHorizontal ?? 20}
            onChange={(value) => onFlapPositionChange(wallKey, index, 'positionHorizontal', value)}
          />
          <RangeEdit
            key={`${item.id}_vertical`}
            title={
              arr.length === 1
                ? t('hole_position_vertical')
                : t('hole_position_vertical') + ' 2'
            }
            min={50}
            step={5}
            max={secondHeight - 13 - item.height}
            unit={'cm'}
            selected={item.positionVertical ?? 50}
            onChange={(value) => onFlapPositionChange(wallKey, index, 'positionVertical', value)}
          />
        </React.Fragment>
      ))}
    </>
  );

  return (
    <React.Fragment>
      {loaded ? (
        <EditableSection
          collapsed={collapsed}
          title={t('window_openings_with_flap')}
          nested
          subsection
          open={false}
        >
          <Spacer>
            {t('please_describe_location_of_window_flaps')}
          </Spacer>

          {/* BACK (max poziomy wg width) */}
          {renderWallControls(
            'back_wall',
            'shutter_back_count',
            'shutter_back_size',
            'shutter_back_material',
            shutter_back_count_available,
            shutter_back_count_selected,
            shutter_back_count_info,
            shutter_back_size_available,
            shutter_back_size_selected,
            shutter_back_size_info,
            shutter_back_material_available,
            shutter_back_material_selected,
            shutter_back_material_info,
            width
          )}

          {/* LEFT (max poziomy wg length) */}
          {renderWallControls(
            'left_wall',
            'shutter_left_count',
            'shutter_left_size',
            'shutter_left_material',
            shutter_left_count_available,
            shutter_left_count_selected,
            shutter_left_count_info,
            shutter_left_size_available,
            shutter_left_size_selected,
            shutter_left_size_info,
            shutter_left_material_available,
            shutter_left_material_selected,
            shutter_left_material_info,
            length
          )}

          {/* RIGHT (max poziomy wg length) */}
          {renderWallControls(
            'right_wall',
            'shutter_right_count',
            'shutter_right_size',
            'shutter_right_material',
            shutter_right_count_available,
            shutter_right_count_selected,
            shutter_right_count_info,
            shutter_right_size_available,
            shutter_right_size_selected,
            shutter_right_size_info,
            shutter_right_material_available,
            shutter_right_material_selected,
            shutter_right_material_info,
            length
          )}

          <Container>
            <ButtonWrapper>
              <Button
                icon={<InfoCircleOutlined />}
                type="primary"
                block
                onClick={openModal}
              >
                {t('more_information')}
              </Button>
            </ButtonWrapper>
          </Container>
        </EditableSection>
      ) : null}
    </React.Fragment>
  );
};

ConstructionExtras.propTypes = {
  collapsed: PropTypes.bool.isRequired,
  setSelected: PropTypes.func.isRequired,
  loaded: PropTypes.bool,
  shutter_back_count_available: PropTypes.arrayOf(availableType),
  shutter_back_count_info: infoShape,
  shutter_back_count_selected: PropTypes.number,
  shutter_back_size_available: PropTypes.arrayOf(availableType),
  shutter_back_size_info: infoShape,
  shutter_back_size_selected: PropTypes.number,
  shutter_back_material_available: PropTypes.arrayOf(availableType),
  shutter_back_material_info: infoShape,
  shutter_back_material_selected: PropTypes.number,
  shutter_left_count_available: PropTypes.arrayOf(availableType),
  shutter_left_count_info: infoShape,
  shutter_left_count_selected: PropTypes.number,
  shutter_left_size_available: PropTypes.arrayOf(availableType),
  shutter_left_size_info: infoShape,
  shutter_left_size_selected: PropTypes.number,
  shutter_left_material_available: PropTypes.arrayOf(availableType),
  shutter_left_material_info: infoShape,
  shutter_left_material_selected: PropTypes.number,
  shutter_right_count_available: PropTypes.arrayOf(availableType),
  shutter_right_count_info: infoShape,
  shutter_right_count_selected: PropTypes.number,
  shutter_right_size_available: PropTypes.arrayOf(availableType),
  shutter_right_size_info: infoShape,
  shutter_right_size_selected: PropTypes.number,
  shutter_right_material_available: PropTypes.arrayOf(availableType),
  shutter_right_material_info: infoShape,
  shutter_right_material_selected: PropTypes.number
};

const mapDispatchToProps = {
  setSelected: setSelectedParameter
};

export default connect(selector, mapDispatchToProps)(withTranslation(ConstructionExtras));
